import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";
import { TileTypes } from "@/types/review.types";

export interface UseTileCommentOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

const formatExtraFields = (data: TileTypes): string => {
  let text = "";
  if ("ambiguousText" in data && data.ambiguousText)
    text += `Ambiguous Text: ${data.ambiguousText}\n\n`;
  if ("complexText" in data && data.complexText) text += `Complex Text: ${data.complexText}\n\n`;
  if ("riskText" in data && data.riskText) text += `Risk Text: ${data.riskText}\n\n`;
  if ("nonStandardText" in data && data.nonStandardText)
    text += `Non-Standard Text: ${data.nonStandardText}\n\n`;
  if ("text1" in data && data.text1) text += `Text 1: ${data.text1}\n\n`;
  if ("text2" in data && data.text2) text += `Text 2: ${data.text2}\n\n`;
  return text;
};

const getSuggestedClause = (data: TileTypes): string | null => {
  if ("suggestedclause" in data && data.suggestedclause) return data.suggestedclause;
  if ("suggestion" in data && data.suggestion) return data.suggestion;
  if ("suggestions" in data && data.suggestions) return data.suggestions;
  if ("suggestedNumber" in data && data.suggestedNumber) return data.suggestedNumber;
  return null;
};

const formatCommentText = (tile: ReviewTileItem): string => {
  let text = "";
  const originalData = tile.originalData;

  if (tile.queryType != null) {
    text += `Type: ${tile.queryType}\n\n`;
  }
  if (tile.impact != null && tile.impact !== "ALL") {
    text += `Impact: ${tile.impact}\n\n`;
  }
  if (tile.clause != null) {
    text += `Clause: ${tile.clause}\n\n`;
  }

  if (originalData != null) {
    text += formatExtraFields(originalData);
  }

  if (tile.reason != null) {
    text += `Reason: ${tile.reason}\n\n`;
  }
  if (tile.analysis != null) {
    text += `Analysis: ${tile.analysis}\n\n`;
  }
  if (tile.explanation != null) {
    text += `Explanation: ${tile.explanation}\n\n`;
  }

  if (originalData != null) {
    if ("riskExplanation" in originalData && originalData.riskExplanation) {
      text += `Risk Explanation: ${originalData.riskExplanation}\n\n`;
    }
    const suggestedClause = getSuggestedClause(originalData);
    if (suggestedClause != null) {
      text += `Suggestion: ${suggestedClause}\n\n`;
    }
  }

  return text.trim();
};

async function addCommentForTile(tile: ReviewTileItem): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.insertComment(formatCommentText(tile));
    await context.sync();
  });
}

async function addBulkComment(tiles: ReviewTileItem[]): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    const combinedComment = tiles
      .map((tile, index) => `${index + 1}. ${formatCommentText(tile)}`)
      .join("\n");
    selection.insertComment(`Review Items:\n${combinedComment}`);
    await context.sync();
  });
}

async function addSequentialComments(tiles: ReviewTileItem[]): Promise<number> {
  let successCount = 0;
  for (const tile of tiles) {
    await addCommentForTile(tile);
    successCount++;
    await new Promise<void>((resolve) => setTimeout(resolve, 100));
  }
  return successCount;
}

export const useTileComment = (options?: UseTileCommentOptions) => {
  const [isAddingComments, setIsAddingComments] = useState(false);
  const [addingCommentTileId, setAddingCommentTileId] = useState<string | null>(null);

  const addSingleComment = async (tile: ReviewTileItem): Promise<boolean> => {
    setAddingCommentTileId(tile.id);
    setIsAddingComments(true);
    try {
      await addCommentForTile(tile);
      options?.onSuccess?.(`Comment added for "${tile.clause ?? tile.id}"`);
      return true;
    } catch (error: unknown) {
      console.error("Error adding comment:", error);
      const msg = error instanceof Error ? error.message : "Unknown error";
      options?.onError?.(`Failed to add comment: ${msg}`);
      return false;
    } finally {
      setAddingCommentTileId(null);
      setIsAddingComments(false);
    }
  };

  const addMultipleComments = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to add as comments");
      return false;
    }
    setIsAddingComments(true);
    try {
      await addBulkComment(tiles);
      options?.onSuccess?.(`${tiles.length} review items added as comments`);
      return true;
    } catch (error: unknown) {
      console.error("Error adding bulk comments:", error);
      const msg = error instanceof Error ? error.message : "Unknown error";
      options?.onError?.(`Failed to add comments: ${msg}`);
      return false;
    } finally {
      setIsAddingComments(false);
    }
  };

  const addCommentsAtSelection = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to add as comments");
      return false;
    }
    setIsAddingComments(true);
    try {
      const count = await addSequentialComments(tiles);
      options?.onSuccess?.(`${count} comments added successfully`);
      return true;
    } catch (error: unknown) {
      console.error("Error adding sequential comments:", error);
      options?.onError?.("Failed to add comments");
      return false;
    } finally {
      setIsAddingComments(false);
    }
  };

  return {
    isAddingComments,
    addingCommentTileId,
    addSingleComment,
    addMultipleComments,
    addCommentsAtSelection,
  };
};
